import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, MaskText, Stagger, StaggerItem } from "./Reveal";

const USES = [
  "Starting your first company.",
  "Trying to turn early traction into a real business.",
  "Launching a new product.",
  "Entering a new market.",
  "Trying to grow without breaking what already works.",
  "Considering outside capital.",
  "Building a team.",
  "Hiring leadership.",
  "Facing a major pivot.",
  "Making a decision that could change the company.",
];

export const WhoFor = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

  return (
    <section id="who" ref={ref} className="relative bg-cream py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-[1300px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-y-14 gap-x-12">
          <div className="lg:col-span-7">
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">Who This Is For</span>
            <h2 className="mt-6 font-serif text-ocean tracking-[-0.02em] leading-[0.98] text-4xl sm:text-5xl lg:text-[4rem]">
              <MaskText lines={["You don't have to be", <span key="z" className="italic text-cognac">starting from zero.</span>]} />
            </h2>
            <Reveal delay={0.1}>
              <p className="mt-6 font-sans text-[11px] uppercase tracking-[0.22em] text-ink/50">Use the framework when you are</p>
            </Reveal>
            <Stagger className="mt-4 grid sm:grid-cols-2 gap-x-10" amount={0.06}>
              {USES.map((u) => (
                <StaggerItem key={u}>
                  <p className="py-3 border-b border-[var(--line)] font-serif text-lg sm:text-xl text-ocean/85 leading-snug">{u}</p>
                </StaggerItem>
              ))}
            </Stagger>
            <Reveal delay={0.1}>
              <p className="mt-10 font-sans text-base text-ink/65">Or simply looking around and thinking:</p>
              <p className="mt-3 font-serif italic text-2xl sm:text-3xl lg:text-4xl text-ocean leading-snug max-w-2xl">
                “What the hell should we actually be focusing on right now?”
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-8">
            <figure className="relative grain overflow-hidden border border-[var(--line)]">
              <motion.img
                style={{ y }}
                src="/images/brian-podcast.png"
                alt="Brian Smith in conversation"
                className="w-full h-[46vh] object-cover object-top img-warm scale-110"
              />
            </figure>
            <Reveal delay={0.1}>
              <div data-testid="company-vs-initiative" className="bg-ocean text-cream p-8 sm:p-10">
                <p className="font-serif text-2xl sm:text-3xl leading-snug">
                  A mature business can still have an <span className="italic text-cognac">infant-stage</span> idea.
                </p>
                <p className="mt-5 font-sans text-sm sm:text-base text-cream/70 leading-relaxed">
                  The framework helps you distinguish between the stage of your{" "}
                  <span className="text-cream uppercase tracking-[0.12em] text-xs">company</span> and the stage of the{" "}
                  <span className="text-cream uppercase tracking-[0.12em] text-xs">initiative</span> you're trying to build.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
